import * as R from 'ramda';

import * as handlers from 'store/shared/accordionBlockHandlers';
import { setAppHasChangesAction } from 'store/unsavedChanges';
import { confirmAlert } from 'utils/alert';
import axios from 'utils/axios';
import { removeAIBlocksFromPage } from 'utils/itineraries/functions/builderUtils';
import { trackEvent, EMixpanelEvents } from 'utils/mixpanel';

import { accountIdSelector, isOwnerSelector } from '../user/selectors';
import { landingDataSelector, pageDataSelector } from './selectors';
import * as utils from './utils';

import { setLandingData, setPageDataAction, resetPageDataAction } from '.';

export const setLandingDataThunk =
  (data, params = {}) =>
  (dispatch) => {
    const { isLocalUpdate = false } = params;
    dispatch(setLandingData(data));
    if (!isLocalUpdate) {
      dispatch(setAppHasChangesAction(true));
    }
  };

export const findPage = (type) => async (dispatch, getStore) => {
  const accountId = accountIdSelector(getStore());
  const isOwner = isOwnerSelector(getStore());

  const statusesQuery = isOwner
    ? 'statuses[]=draft&statuses[]=pending&statuses[]=rejected&statuses[]=approved'
    : 'statuses[]=draft&statuses[]=rejected&statuses[]=approved';

  return await axios.get(`/acc/${accountId}/pages?${statusesQuery}&types[]=${type}`).then(({ data }) => {
    return data;
  });
};

export const getPage = (id, withStaff) => async (dispatch, getStore) => {
  const accountId = accountIdSelector(getStore());
  const data = await axios
    .get(`/acc/${accountId}/pages/${id}${withStaff ? '?with_staff=1' : ''}`)
    .then(({ data = {} }) => data);
  return data;
};

export const getPageBlocks = (id) => async (dispatch, getStore) => {
  const accountId = accountIdSelector(getStore());
  const data = await axios.get(`/acc/${accountId}/pages/${id}/blocks`).then(({ data = {} }) => data);
  return data;
};

export const editPageThunk = (page) => async (dispatch, getStore) => {
  const accountId = accountIdSelector(getStore());
  const handeledPage = removeAIBlocksFromPage(page);
  trackEvent(EMixpanelEvents.UPDATE_PAGE, handeledPage);

  const info = await axios
    .patch(`/acc/${accountId}/pages/${handeledPage.id}`, handeledPage)
    .then(({ data = {} }) => data);

  dispatch(setPageDataAction(info));
  return info;
};

export const getPageDataThunk = (pageId) => async (dispatch, getStore) => {
  const accountId = accountIdSelector(getStore());

  const data = await axios.get(`/acc/${accountId}/pages/${pageId}`).then(({ data = {} }) => data);

  dispatch(setPageDataAction(data));
  return data;
};

export const createPageThunk = (page) => async (dispatch, getStore) => {
  const accountId = accountIdSelector(getStore());
  const handeledPage = removeAIBlocksFromPage(page);
  trackEvent(EMixpanelEvents.CREATE_PAGE, handeledPage);
  const info = await axios.post(`/acc/${accountId}/pages`, handeledPage).then(({ data = {} }) => data);

  dispatch(setPageDataAction(info));

  return info;
};

export const publishPageThunk = () => async (dispatch, getStore) => {
  const accountId = accountIdSelector(getStore());
  const currentPage = pageDataSelector(getStore());

  const page = await axios.put(`/acc/${accountId}/pages/${currentPage.id}/pending`).then(({ data = {} }) => data);

  dispatch(setPageDataAction(page));
  dispatch(setAppHasChangesAction(false));

  return page;
};

export const savePageThunk = (page) => async (dispatch, getStore) => {
  const currentPage = pageDataSelector(getStore());

  const savedPage = currentPage.id
    ? await dispatch(editPageThunk({ ...page, id: currentPage.id }))
    : await dispatch(createPageThunk(page));

  dispatch(setAppHasChangesAction(false));

  return { ...savedPage, pageId: savedPage.id };
};

export const deletePageThunk = (pageId) => async (dispatch, getStore) => {
  const accountId = accountIdSelector(getStore());
  await axios.delete(`/acc/${accountId}/pages/${pageId}`);
  dispatch(resetPageDataAction());
  dispatch(setAppHasChangesAction(false));
};

export const updateLandingContentThunk = (mockData) => async (dispatch) => {
  dispatch(setLandingDataThunk(mockData));
};

export const removeLandingBlockThunk =
  ({ pageIndex, blockIndex }) =>
  async (dispatch, getStore) => {
    const data = landingDataSelector(getStore());
    const currentBlock = data?.pages?.[pageIndex]?.blocks?.[blockIndex];

    const updatedPages = utils.removeBlockInsidePage({ data, pageIndex, blockIndex });

    const showAlert =
      currentBlock?.type === 'image' ||
      currentBlock?.type === 'embed' ||
      currentBlock?.type === 'document' ||
      currentBlock?.type === 'accordion';

    if (showAlert) {
      confirmAlert({
        title: 'Are you sure you want to delete this element?',
        onConfirm: () => dispatch(updateLandingContentThunk({ pages: updatedPages })),
      });
    } else {
      dispatch(updateLandingContentThunk({ pages: updatedPages }));
    }
  };

export const addLandingBlockThunk =
  ({ pageIndex, blockIndex, newObject: blockOrBlocks }) =>
  async (dispatch, getStore) => {
    const data = landingDataSelector(getStore());

    const updatedPages = utils.addBlocksInsidePage({ data, pageIndex, blockIndex, blockOrBlocks });

    dispatch(updateLandingContentThunk({ pages: updatedPages }));
  };

export const editLandingBlockThunk =
  ({ pageIndex, blockIndex, newObject }) =>
  async (dispatch, getStore) => {
    const data = landingDataSelector(getStore());

    if (R.isNil(data?.pages)) {
      return;
    }

    const updatedPages = utils.editBlockInsidePage({ data, pageIndex, blockIndex, newBlock: newObject });

    dispatch(updateLandingContentThunk({ pages: updatedPages }));
  };

export const addLandingBlockInsideAccordionThunk =
  ({ pageIndex, blockIndex, newObject: blockOrBlocks, spoilerIndex, spoilerBlockIndex, insertPosition }) =>
  async (dispatch, getStore) => {
    const data = landingDataSelector(getStore());
    const updatedPages = handlers.addBlockInsideAccordionHandler({
      data,
      pageIndex,
      blockIndex,
      blockOrBlocks,
      spoilerIndex,
      spoilerBlockIndex,
      insertPosition,
    });

    if (!updatedPages) return;

    dispatch(updateLandingContentThunk({ pages: updatedPages }));
  };

export const removeLandingBlockInsideAccordionThunk =
  ({ pageIndex, blockIndex, spoilerIndex, spoilerBlockIndex }) =>
  async (dispatch, getStore) => {
    const data = landingDataSelector(getStore());

    const res = handlers.removeBlockInsideAccordionHandler({
      data,
      pageIndex,
      blockIndex,
      spoilerIndex,
      spoilerBlockIndex,
    });

    if (!res.updatedPages) return;

    if (res.showAlert) {
      confirmAlert({
        title: 'Are you sure you want to delete this element?',
        onConfirm: () => dispatch(updateLandingContentThunk({ pages: res.updatedPages })),
      });
    } else {
      dispatch(updateLandingContentThunk({ pages: res.updatedPages }));
    }
  };

export const editLandingBlockInsideAccordionThunk =
  ({ pageIndex, blockIndex, spoilerIndex, spoilerBlockIndex, newObject }) =>
  async (dispatch, getStore) => {
    const data = landingDataSelector(getStore());
    const updatedPages = handlers.editBlockInsideAccordionHandler({
      data,
      pageIndex,
      blockIndex,
      spoilerIndex,
      spoilerBlockIndex,
      newObject,
    });

    if (!updatedPages) return;

    dispatch(updateLandingContentThunk({ pages: updatedPages }));
  };

export const editLeftSideSubheadlineThunk =
  ({ subheadline }) =>
  async (dispatch, getStore) => {
    const data = landingDataSelector(getStore());

    if (R.isNil(data?.pages)) {
      return;
    }

    dispatch(
      updateLandingContentThunk({
        pages: [
          {
            ...data.pages[0],
            subheadline,
          },
        ],
      }),
    );
  };

export const updateLandingPageStylesThunk =
  ({ pageIndex, customStyles }) =>
  async (dispatch, getStore) => {
    const data = landingDataSelector(getStore());

    const updateStyles = (data) => ({ ...data, customStyles });
    const newData = R.adjust(pageIndex, updateStyles, data?.pages || []);

    dispatch(updateLandingContentThunk({ pages: newData }));
  };

export const updateLandingPageFontsThunk =
  ({ pageIndex, customFonts }) =>
  async (dispatch, getStore) => {
    const data = landingDataSelector(getStore());

    const updateFonts = (data) => ({ ...data, customFonts });
    const newData = R.adjust(pageIndex, updateFonts, data?.pages);

    dispatch(updateLandingContentThunk({ pages: newData }));
  };
